import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface Admin {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'SUPER_ADMIN' | 'ADMIN';
  isActive: boolean;
  lastLogin?: string;
}

interface AuthContextType {
  admin: Admin | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('adminToken');
      if (savedToken) {
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
          // Use /auth/me or /admin/profile endpoint
          const response = await api.get('/admin/profile');
          const adminData = response.data.data;
          
          // Verify user is admin (check both lowercase and uppercase)
          const role = adminData.role?.toLowerCase();
          if (role !== 'admin' && role !== 'super_admin') {
            throw new Error('Access denied: Admin role required');
          }
          
          setAdmin({
            id: adminData._id || adminData.id,
            firstName: adminData.firstName,
            lastName: adminData.lastName,
            email: adminData.email,
            role: role === 'super_admin' ? 'SUPER_ADMIN' : 'ADMIN',
            isActive: adminData.isActive !== false,
            lastLogin: adminData.lastLogin,
          });
          setToken(savedToken);
        } catch (error: any) {
          const status = error.response?.status;
          
          if (status === 401 || status === 403) {
            // Token is invalid - clear it
            localStorage.removeItem('adminToken');
            delete api.defaults.headers.common['Authorization'];
            setAdmin(null);
            setToken(null);
          } else if (status === 404) {
            // 404 = Admin profile endpoint issue - clear token, need to login again
            localStorage.removeItem('adminToken');
            delete api.defaults.headers.common['Authorization'];
            setAdmin(null);
            setToken(null);
          }
          // For network errors or 5xx, keep token for retry
        }
      } else {
        // No token found, ensure clean state
        setAdmin(null);
        setToken(null);
      }
      setLoading(false);
    };

    initAuth().catch((error) => {
      console.error('Error initializing auth:', error);
      setLoading(false);
    });
  }, []);

  const login = async (email: string, password: string) => {
    try {
      // Use /admin/signin endpoint for admin authentication
      const response = await api.post('/admin/signin', { email, password });
      const { admin: adminData, token: tokenData } = response.data.data;
      
      setAdmin({
        id: adminData.id || adminData._id,
        firstName: adminData.firstName,
        lastName: adminData.lastName,
        email: adminData.email,
        role: adminData.role || 'ADMIN',
        isActive: adminData.isActive !== false,
        lastLogin: adminData.lastLogin,
      });
      setToken(tokenData);
      localStorage.setItem('adminToken', tokenData);
      api.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to blacklist refresh token
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setAdmin(null);
      setToken(null);
      localStorage.removeItem('adminToken');
      delete api.defaults.headers.common['Authorization'];
    }
  };

  const value = {
    admin,
    token,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};